import { cn } from '@/lib/utils';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, CircleDot, CloudUpload, Database, DatabaseBackup, FileUp, Loader2, Lock, Rocket, Search, Send, ShieldCheck, Wrench } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Poczekalnia } from '@/components/Poczekalnia';
import { WstrzymaneUsuniecia } from '@/components/WstrzymaneUsuniecia';
import { fetchWstrzymane } from '@/lib/wstrzymane-api';
import { RozjazdSzczegoly } from '@/components/RozjazdSzczegoly';
import { TokenChmurowegoMastera } from '@/components/TokenChmurowegoMastera';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { acceptAgent, fetchMachineTasks, listAgents, type MachineTask, wgrajKopie, zlecOdtworzenie, zlecPobranieKopii } from '@/lib/agents-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { paczkiNaDysku } from '@/lib/kopie-na-dysku';
import { fetchWdrozenie, odhaczKrok, type KrokWdrozenia, type StanWdrozenia } from '@/lib/wdrozenie-api';
import { fetchSyncTables, poprosOPelnyOdczyt, przerwijPelnyOdczyt } from '@/lib/sync-tables-api';
import { Tabs } from '@/components/ui/tabs';
import { ZajetoscAutomatow } from '@/components/ZajetoscAutomatow';
import { StanTabel } from '@/components/StanTabel';
import { PrzebiegUzgadniajacy } from '@/components/PrzebiegUzgadniajacy';
import { FormularzNadrobienia, PasekNadrobienia, ZalegleWydania } from '@/components/ZalegleWydania';
import { fetchPoczekalnia } from '@/lib/agents-api';
import { ustawRozsylanieWydan, ustawTrybWyspy } from '@/lib/wyspa-api';

/**
 * SMART SYNC — WDROŻENIE OD ZERA, SIEDEM KROKÓW W JEDNYM MIEJSCU.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO Z TEGO, ŻE INSTRUKCJA WDROŻENIA MA 39 KB I DZIESIĘĆ ETAPÓW, a czynności, które
 *  opisuje, są rozrzucone po pięciu ekranach portalu (Agenci, Ustawienia, Kopie, Automaty, Pulpit).
 *  Technik czytał dokument na drugim monitorze i przeskakiwał między zakładkami, zgadując, czy to,
 *  co widzi, znaczy „gotowe". Zgłoszone wprost: „ma być tak, żeby nie dało się tego spierdolić".
 *
 *  ⚠️ EKRAN NIE LICZY STANU. Wszystkie siedem kroków przychodzi gotowych z instancji
 *  (`api/src/wdrozenie/kroki-wdrozenia.ts`) — razem z rozstrzygnięciem, KTÓRY przycisk wolno
 *  kliknąć. Kolejność kroków jest treścią poprawności: pomylenie dwóch z nich kosztuje ponowną
 *  instalację całej floty albo nieodwracalne zasianie mastera z uboższej bazy. Reguły tej klasy
 *  nie mogą istnieć w dwóch miejscach.
 *
 *  ⚠️ KREATOR POKAZUJE STAN, A NIE LICZY KLIKNIĘĆ. Wyspa, która już działa, świeci tu cała na
 *  zielono bez klikania czegokolwiek — także wtedy, gdy wdrożono ją ręcznie przed powstaniem tego
 *  ekranu. Licznik kroków twierdziłby o działającym wdrożeniu, że jest niedokończone.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Odświeżanie: wdrożenie to czynności, które trwają minuty (kopia, restore, start sidecara). */
const ODSWIEZANIE_MS = 15_000;

/**
 * ILE PACZEK Z MAGAZYNU POKAZUJEMY NA TYM EKRANIE.
 *
 * ⚠️ KRÓTKA LISTA, BO TO KREATOR, NIE EKRAN KOPII. Przy zasianiu mastera w grę wchodzi paczka
 * NAJŚWIEŻSZA albo ta właśnie wgrana; pełna historia (z sumami, przerywaniem zawieszonych zadań
 * i kopiami na dyskach maszyn) jest na ekranie Kopie i tam prowadzi odnośnik pod spodem.
 */
const PACZEK_NA_EKRANIE = 5;

const znacznik = (stan: KrokWdrozenia['stan']) => {
  switch (stan) {
    case 'ZROBIONE':
      return { ikona: Check, klasa: 'text-[var(--success)] border-[var(--success)]/40 bg-[var(--success)]/10' };
    case 'TERAZ':
      return { ikona: CircleDot, klasa: 'text-primary border-primary/50 bg-primary/10' };
    case 'UWAGA':
      return { ikona: AlertTriangle, klasa: 'text-[var(--warning)] border-[var(--warning)]/40 bg-[var(--warning)]/10' };
    default:
      return { ikona: Lock, klasa: 'text-muted-foreground border-border bg-muted/30' };
  }
};

function Krok(props: {
  numer: number;
  krok: KrokWdrozenia;
  tytul: string;
  /** Zdanie „po co ten krok" — dla kogoś, kto widzi ten ekran pierwszy raz. */
  poCo: string;
  children?: React.ReactNode;
}) {
  const { t } = useI18n();
  const { ikona: Ikona, klasa } = znacznik(props.krok.stan);
  return (
    <section
      className={`rounded-lg border p-4 ${props.krok.stan === 'CZEKA' ? 'border-border opacity-70' : 'border-border'}`}
    >
      <header className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${klasa}`}
        >
          {props.krok.stan === 'ZROBIONE' ? <Ikona className="size-4" /> : props.numer}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            {props.tytul}
            <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${klasa}`}>
              {t(`ss.stan.${props.krok.stan}`)}
            </span>
          </h2>
          <p className="text-[11px] leading-snug text-muted-foreground">{props.poCo}</p>
          {props.krok.powody.map((p) => (
            <p key={p} className="text-xs leading-snug">
              {p}
            </p>
          ))}
          {props.children !== undefined && <div className="pt-2">{props.children}</div>}
        </div>
      </header>
    </section>
  );
}

/**
 * NAZWA SPRAWDZENIA, PRZY KTÓRYM STAWIAMY PRZYCISK SZCZEGÓŁÓW.
 *
 * ⚠️⚠️ TO NAPIS Z API (`kroki-wdrozenia.ts`), więc jego zmiana po tamtej stronie ZGASI przycisk
 * i nikt tego nie zauważy — dlatego pilnuje tego test kontraktowy `rozjazd-sprawdzenie.spec.ts`,
 * który czyta plik API. Ta sama pułapka złapała nas już raz przy nazwie przycisku wyrównania
 * (29.08.2026).
 */
export const NAZWA_SPRAWDZENIA_ROZJAZDU = 'Konfiguracja maszyn zgodna';
/**
 * ⚠️⚠️ DRUGIE SPRAWDZENIE O ROZJEźDZIE — to, które świeci, ale NIE blokuje (09.09.2026).
 * Przycisk „pokaż rozjazd" musi stać przy obu, bo okno pokazuje jedno i drugie. Napis jest
 * umówiony z API i pilnuje tego `nazwa-sprawdzenia-rozjazdu.spec.ts`.
 */
export const NAZWA_SPRAWDZENIA_POZA_WYROWNANIEM = 'Rozjazd poza zasięgiem wyrównania';

export function SmartSync() {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const { groupId } = useIslands();
  const toast = useToast();
  const qc = useQueryClient();

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  DWA RÓŻNE ZADANIA NA JEDNYM EKRANIE — rozdzielone zakładkami 21.09.2026.
   *
   *  ⚠️⚠️ ZGŁOSZONE Z WDROŻEŃ: „wszystko do naprawy jest w tym samym oknie co wdrożenie". Kreator
   *  prowadzi człowieka, który stawia Smart Sync PIERWSZY RAZ i ma iść krok po kroku. Poczekalnia,
   *  stan tabel i pełny odczyt to narzędzia do wyspy, która już działa i się POPSUŁA —
   *  narzędzia ratunkowe wplecione w instrukcję montażu czyta się jak część instrukcji.
   *
   *  ⚠️⚠️ ZAKŁADKA NIE MOŻE JEDNAK CHOWAĆ PROBLEMU, i to jest cała trudność tego podziału. Dlatego
   *  „Synchronizacje" niesie LICZNIK węzłów w poczekalni: człowiek przy kreatorze nie ma się czym
   *  rozpraszać, ale widzi, że po drugiej stronie coś czeka. Zakładka bez tego licznika byłaby
   *  schowkiem, a nie porządkiem.
   *
   *  ⚠️ ZAKŁADKA W ADRESIE, tak samo jak na karcie automatu: „zobacz, co mi pokazuje poczekalnia"
   *  ma dać się wysłać linkiem, a odświeżenie strony nie może wracać do kreatora.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const [parametry, ustawParametry] = useSearchParams();
  const zakladka: 'wdrozenie' | 'synchronizacje' =
    parametry.get('zakladka') === 'synchronizacje' ? 'synchronizacje' : 'wdrozenie';

  /** „Uzgodnij” w stanie tabel ustawia tabelę w przebiegu i przewija do niego — patrz niżej. */
  const [doUzgodnienia, setDoUzgodnienia] = useState<{ sps: string } | null>(null);
  const przebiegRef = useRef<HTMLDivElement>(null);

  /**
   * ⚠️ TEN SAM KLUCZ ZAPYTANIA CO W `Poczekalnia` — React Query oddaje jedną odpowiedź obu
   * miejscom, więc licznik przy zakładce nie kosztuje ani jednego dodatkowego wywołania.
   */
  const poczekalnia = useQuery({
    queryKey: ['poczekalnia', groupId],
    queryFn: () => fetchPoczekalnia(groupId!),
    enabled: groupId !== null,
    refetchInterval: 30_000,
  });
  const odlozonych = (poczekalnia.data?.wezly ?? []).filter((w) => w.wPoczekalni).length;
  /** ⚠️ Ten sam klucz co w `WstrzymaneUsuniecia` — licznik przy zakładce bez dodatkowego wywołania. */
  const wstrzymane = useQuery({
    queryKey: ['wstrzymane', groupId],
    queryFn: () => fetchWstrzymane(groupId!),
    enabled: groupId !== null,
    refetchInterval: 30_000,
  });
  const doDecyzji = odlozonych + (wstrzymane.data?.wstrzymane.length ?? 0);

  const stan = useQuery({
    queryKey: ['wdrozenie', groupId],
    queryFn: () => fetchWdrozenie(groupId!),
    enabled: groupId !== null,
    refetchInterval: ODSWIEZANIE_MS,
  });
  /**
   * ⚠️⚠️ TA LISTA MUSI ODŚWIEŻAĆ SIĘ RAZEM Z KREATOREM. Zgłoszone 01.09.2026: „w Smart Sync dość
   * długo czasu mija, zanim już potwierdzony agent też tam się pojawi na liście". Powód: ekran
   * Agentów trzyma ten sam klucz zapytania ze `staleTime: 300_000`, więc kreator dostawał z pamięci
   * odpowiedź sprzed pięciu minut — a to na tym ekranie czeka się na pojawienie się węzła.
   */
  const agenci = useQuery({
    queryKey: ['agents'],
    queryFn: () => listAgents(),
    enabled: groupId !== null,
    refetchInterval: ODSWIEZANIE_MS,
    staleTime: 0,
  });
  const zadania = useQuery({
    queryKey: ['machine-tasks', groupId],
    queryFn: () => fetchMachineTasks(groupId!),
    enabled: groupId !== null,
    refetchInterval: ODSWIEZANIE_MS,
  });

  const odswiez = async () => {
    await qc.invalidateQueries({ queryKey: ['wdrozenie', groupId] });
    await qc.invalidateQueries({ queryKey: ['machine-tasks', groupId] });
    await qc.invalidateQueries({ queryKey: ['agents'] });
  };

  const odhacz = useMutation({
    mutationFn: (v: { krok: 'PRZYGOTOWANIE' | 'MASZYNY_KOMPLET'; wlaczone: boolean }) =>
      odhaczKrok(groupId!, v.krok, v.wlaczone),
    onSuccess: odswiez,
    onError: (e: Error) => toast.error(e.message),
  });

  const przelaczTryb = useMutation({
    mutationFn: () => ustawTrybWyspy(groupId!, 'SMART_SYNC'),
    onSuccess: async () => {
      toast.success(t('ss.tryb.zmieniony'));
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wlaczRozsylanie = useMutation({
    mutationFn: () => ustawRozsylanieWydan(groupId!, true),
    onSuccess: async () => {
      toast.success(t('ss.rozsylanie.wlaczone'));
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const potwierdz = useMutation({
    mutationFn: (agentId: string) => acceptAgent(agentId),
    onSuccess: async () => {
      toast.success(t('ss.k4.potwierdzony'));
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ „ROZEŚLIJ ZALEGŁE WYDANIA OD", jego przerwanie i granica czasu mieszkają w `ZalegleWydania.tsx`
   * (29.09.2026): kreator daje formularz tylko przed pierwszym przebiegiem, potem samą belkę.
   */

  const naChmure = useMutation({
    mutationFn: (v: { chmuraId: string; zadanieId: string }) => zlecPobranieKopii(v.chmuraId, groupId!, v.zadanieId),
    onSuccess: async () => {
      toast.success(t('ss.paczka.zlecona'));
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  WGRANIE PACZKI Z KOMPUTERA — TU, NIE NA INNYM EKRANIE.
   *
   *  ⚠️⚠️ ZGŁOSZONE (31.08.2026): „zróbmy w tej bazie do chmury, żeby wszystko dało się załatwić
   *  na tym ekranie: upload pliku, wylistować go i dać opcję wgrania na bazę w chmurze (…) skoro
   *  można pobrać agentów z tego ekranu, to upload też musi w pełni działać".
   *
   *  ⚠️ POPRZEDNIA WERSJA ODSYŁAŁA NA EKRAN KOPII i to było gorsze niż wygląda: wdrożenie Smart
   *  Sync idzie z pliku `.bak`/`.zip` w większości przypadków, więc GŁÓWNA droga wymagała wyjścia
   *  z kreatora, znalezienia właściwego przycisku wśród czterech i powrotu.
   *
   *  ⚠️ PACZKĘ PRZYPISUJEMY WĘZŁOWI CHMUROWEMU, gdy jest — bo to na jego dysk pojedzie. Gdy go
   *  jeszcze nie ma (krok 4 przed nami), bierzemy dowolny potwierdzony węzeł: przypisanie jest
   *  ETYKIETĄ w magazynie, nie adresem dostawy, więc blokowanie wgrania z tego powodu odbierałoby
   *  technikowi możliwość przygotowania pliku zawczasu.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const plikRef = useRef<HTMLInputElement>(null);
  const [rozjazdOtwarty, setRozjazdOtwarty] = useState(false);
  const [wysylka, setWysylka] = useState<{ nazwa: string; procent: number } | null>(null);
  const wgraj = useMutation({
    mutationFn: async (v: { agentId: string; plik: File }) => {
      setWysylka({ nazwa: v.plik.name, procent: 0 });
      return wgrajKopie(v.agentId, groupId!, v.plik, (procent) => setWysylka({ nazwa: v.plik.name, procent }));
    },
    onSuccess: async (r) => {
      toast.success(t('ss.k5.wgrano').replace('{plik}', r.nazwa).replace('{mb}', String(Math.round(r.rozmiar / 1_048_576))));
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setWysylka(null),
  });

  const [bazaDocelowa, setBazaDocelowa] = useState('');
  const odtworz = useMutation({
    mutationFn: (v: { chmuraId: string; nazwa: string }) =>
      zlecOdtworzenie(v.chmuraId, groupId!, v.nazwa, bazaDocelowa.trim()),
    onSuccess: async () => {
      toast.success(t('ss.odtworzenie.zlecone'));
      setBazaDocelowa('');
      await odswiez();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (groupId === null) return <p className="p-4 text-sm text-muted-foreground">{t('ss.brakWyspy')}</p>;
  if (stan.isPending) return <p className="p-4 text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (stan.isError) return <p className="p-4 text-sm text-[var(--destructive)]">{(stan.error as Error).message}</p>;

  const d: StanWdrozenia = stan.data;
  /** Rozjazd węzłów MIĘDZY SOBĄ (czujnik geometrii) — skrót do jego okna stoi w stanie tabel. */
  const rozjazdMiedzyWezlami = {
    tabel: new Set(d.rozjazdy.map((r) => r.tabela)).size,
    automatow: new Set(d.rozjazdy.map((r) => r.vmid)).size,
  };
  const krok = (id: KrokWdrozenia['id']) => d.kroki.find((k) => k.id === id)!;
  const wiersze = agenci.data?.agents ?? [];
  const wszystkieZadania: MachineTask[] = zadania.data ?? [];

  /**
   * WĘZEŁ CHMUROWY — bierzemy go Z ODPOWIEDZI PORTALU, a nie rozpoznajemy sami.
   *
   * ⚠️⚠️ ZGŁOSZONE 01.09.2026: „po wgraniu kopii nie można odtworzyć bazy w chmurze". Krok 5 pisał
   * „Paczka leży na węźle chmurowym i czeka na odtworzenie", a krok 6 obok — „Na węźle chmurowym
   * nie ma jeszcze paczki", z wyszarzonym przyciskiem. Przyczyna: portal i ekran rozpoznawały
   * chmurę na dwa różne sposoby, a prawdziwa reguła (brak automatu ORAZ brak VMID-u) była w trzecim
   * miejscu. Teraz jest jedna, po stronie portalu: `chmuraNodeId`.
   *
   * ⚠️ `chmuraNodeId === undefined` to starsza instancja — wtedy zostaje dawne rozpoznanie, żeby
   * ekran nie przestał działać w czasie wdrożenia nowej wersji.
   */
  /**
   * ILE WĘZŁÓW DOCIĄGA HISTORIĘ — po granicy żywych wydań, nie po samym kursorze.
   *
   * ⚠️ LICZYMY WĘZŁY, NIE WIERSZE. Odległość w numerach nie jest liczbą wierszy (numery mają
   * dziury, a z paczki węzła wypadają jego własne wydania), a zmyślona precyzja w takim miejscu
   * jest gorsza niż jej brak.
   */
  const dociaganie = wiersze.filter(
    (a) => a.wydaniaZyweOd != null && a.wydaniaDolSeq != null && a.wydaniaZyweOd > a.wydaniaDolSeq,
  ).length;

  const chmura =
    d.chmuraNodeId === undefined
      ? wiersze.find((a) => a.machine === null && d.wezly.some((w) => w.nodeId === a.nodeId))
      : d.chmuraNodeId === null
        ? undefined
        : wiersze.find((a) => a.nodeId === d.chmuraNodeId);
  /**
   * ⚠️ KOPIOWANIE Z MASZYNY ZDJĘTE Z TEGO EKRANU (31.08.2026) — razem z wyborem źródła, wskazaniem
   * „najbogatszej konfiguracji" i zlecaniem `KOPIA_BAZY`. Ta robota żyje na ekranie Kopie, gdzie
   * widać też historię zadań i da się przerwać zawieszone; tutaj był trzecim przyciskiem, przez
   * który trzeba było przejść wzrokiem, szukając tego jednego, którego się używa.
   */

  /**
   * NAJŚWIEŻSZA UDANA PACZKA W MAGAZYNIE — to ją wysyłamy na węzeł chmurowy.
   *
   * ⚠️⚠️ `KOPIA_WGRANA` LICZY SIĘ TAK SAMO JAK `KOPIA_BAZY`, i bez tego cała droga „z pliku" była
   * ślepa: paczka wgrana z komputera operatora ląduje w magazynie z wynikiem o DOKŁADNIE tym
   * samym kształcie (`objectKey`, `nazwa`, `size`, `suma`), API przyjmuje ją do pobrania na węzeł
   * bez rozróżnienia — a ten ekran jej nie widział i pisał „nie ma jeszcze gotowej kopii".
   * Zgłoszone 31.08.2026: większość wdrożeń Smart Sync idzie właśnie z pliku `.bak`/`.zip`.
   */
  const gotowePaczki = wszystkieZadania
    .filter((z) => (z.kind === 'KOPIA_BAZY' || z.kind === 'KOPIA_WGRANA') && z.status === 'DONE')
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  /** Węzeł, któremu przypisujemy wgrywaną paczkę — patrz uzasadnienie przy `wgraj`. */
  const wezelDlaPaczki = chmura ?? wiersze.find((a) => a.machine !== null) ?? null;

  /**
   * ⚠️ SPIS PACZEK NA WĘŹLE CHMUROWYM LICZYMY Z ZADAŃ, nie ze spisu agenta — na świeżym masterze
   * agent nie ma udanego cyklu (bazy jeszcze nie ma), więc spisu nie przysyła. Powód opisany
   * w `kopie-na-dysku.ts`; bez tego przycisk „Odtwórz" nie miałby przy czym się pojawić.
   */
  const naDysku =
    chmura === undefined
      ? null
      : paczkiNaDysku({
          spis: chmura.localBackups,
          spisZ: chmura.diskAt ?? null,
          zadania: wszystkieZadania,
          agentId: chmura.id,
        }).lista;
  const paczka = naDysku?.[0] ?? null;

  /** Ten sam wzorzec, co w API i w agencie: nazwa wchodzi do treści `RESTORE DATABASE [...]`. */
  const nazwaBazyOk = /^[A-Za-z_][A-Za-z0-9_$-]*$/.test(bazaDocelowa.trim());
  /**
   * ⚠️ ŻĄDAMY PRZEPISANIA NAZWY, zamiast podstawić ją z wyspy. Odtworzenie odmawia na nazwę, która
   * już istnieje, więc drugiego podejścia nie ma: pomyłka kosztuje nową nazwę bazy i zmianę
   * `SPS_DB` po stronie BillVisa. Przepisanie jest jedynym momentem, w którym człowiek na tę nazwę
   * naprawdę patrzy.
   */
  const nazwaZgodna =
    d.wyspa.masterDatabase !== null &&
    bazaDocelowa.trim().toLowerCase() === d.wyspa.masterDatabase.toLowerCase();

  return (
    <div className="space-y-4 p-4">
      {oknoPytania}
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-base font-semibold">
          <Rocket className="size-5" />
          {t('ss.title')}
        </h1>
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">{d.wyspa.name}</span> ({d.wyspa.code})
        </p>
        {/*
          ⚠️⚠️ „SYNCHRONIZACJA W TRAKCIE" PRZY TYTULE — zamówione 02.09.2026: „czy możemy gdzieś
          u góry przy «SMART SYNC» dodać ikonkę, że synchronizacja jest w trakcie?". Powód jest
          mocniejszy niż wygoda: dociąganie historii po przewinięciu trwa GODZINY i wygląda
          identycznie jak zator, jeśli nikt tego nie napisze. Zdanie mówi też to, co najważniejsze
          — że nowe wydania jadą normalnie, więc limity liczą się z bieżących.
        */}
        {dociaganie > 0 && (
          <p className="flex w-fit items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-xs">
            <Loader2 className="size-3.5 animate-spin text-primary" />
            {t('ss.dociaganie').replace('{wezly}', String(dociaganie))}
          </p>
        )}
      </header>

      {/*
        ⚠️ PASEK POD NAGŁÓWKIEM, A NAGŁÓWEK ZOSTAJE NAD NIM: nazwa i kod wyspy obowiązują w OBU
        zakładkach — schowanie ich do jednej znaczyłoby, że na drugiej nie wiadomo, o której wyspie
        mowa. Ten sam układ co na karcie automatu.
      */}
      <Tabs
        zakladki={[
          { id: 'wdrozenie', etykieta: t('ss.zakladka.wdrozenie'), ikona: <Rocket className="size-4" /> },
          {
            id: 'synchronizacje',
            etykieta: t('ss.zakladka.synchronizacje'),
            ikona: <Wrench className="size-4" />,
            /**
             * ⚠️ Zero NIE pokazujemy — licznik ma znaczyć „jest tam coś", a nie stać zawsze.
             * Liczy odłożone węzły i wstrzymane zdjęcia: jedno i drugie czeka tam na człowieka.
             */
            licznik: doDecyzji > 0 ? doDecyzji : null,
          },
        ]}
        wybrana={zakladka}
        onWybor={(z) => ustawParametry(z === 'wdrozenie' ? {} : { zakladka: z }, { replace: true })}
      />

      {zakladka === 'wdrozenie' && (
      <div className="space-y-3">
        {/* ── 1 ───────────────────────────────────────────────────────────── */}
        <Krok numer={1} krok={krok('PRZYGOTOWANIE')} tytul={t('ss.k1.title')} poCo={t('ss.k1.poCo')}>
          <ul className="mb-2 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            <li>{t('ss.k1.p1')}</li>
            <li>{t('ss.k1.p2')}</li>
            <li>{t('ss.k1.p3')}</li>
          </ul>
          {(() => {
            const o = d.odhaczenia.find((x) => x.krok === 'PRZYGOTOWANIE');
            return o === undefined ? (
              <Button
                size="sm"
                disabled={!krok('PRZYGOTOWANIE').wolno || odhacz.isPending}
                onClick={() => odhacz.mutate({ krok: 'PRZYGOTOWANIE', wlaczone: true })}
              >
                <Check className="size-4" />
                {t('ss.k1.potwierdz')}
              </Button>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                {t('ss.potwierdzil')
                  .replace('{kto}', o.byEmail)
                  .replace('{kiedy}', new Date(o.at).toLocaleString())}{' '}
                <button
                  type="button"
                  className="underline decoration-dotted hover:text-foreground"
                  onClick={() => odhacz.mutate({ krok: 'PRZYGOTOWANIE', wlaczone: false })}
                >
                  {t('ss.cofnij')}
                </button>
              </p>
            );
          })()}
        </Krok>

        {/* ── 2 ───────────────────────────────────────────────────────────── */}
        <Krok numer={2} krok={krok('AGENCI')} tytul={t('ss.k2.title')} poCo={t('ss.k2.poCo')}>
          <div className="space-y-2">
            <p className="text-xs">
              <Link to="/agenci" className="inline-flex items-center gap-1 text-primary underline">
                {t('ss.k2.doAgentow')}
                <ArrowRight className="size-3" />
              </Link>
            </p>
            <ul className="space-y-0.5 text-[11px] text-muted-foreground">
              {d.wezly
                .filter((w) => w.maAutomat)
                .map((w) => (
                  <li key={w.nodeId}>
                    <span className="font-medium text-foreground">{w.nodeId}</span>
                    {w.bazaLokalna === null ? ` — ${t('ss.bezBazy')}` : ` — ${w.bazaLokalna}`}
                    {w.potwierdzony ? '' : ` · ${t('ss.czekaNaPotwierdzenie')}`}
                    {w.wersja === null ? '' : ` · ${w.wersja}`}
                  </li>
                ))}
            </ul>
            {(() => {
              const o = d.odhaczenia.find((x) => x.krok === 'MASZYNY_KOMPLET');
              return o === undefined ? (
                <Button
                  size="sm"
                  disabled={!krok('AGENCI').wolno || odhacz.isPending || d.wezly.filter((w) => w.maAutomat).length === 0}
                  onClick={() => odhacz.mutate({ krok: 'MASZYNY_KOMPLET', wlaczone: true })}
                >
                  <Check className="size-4" />
                  {t('ss.k2.komplet')}
                </Button>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  {t('ss.potwierdzil')
                    .replace('{kto}', o.byEmail)
                    .replace('{kiedy}', new Date(o.at).toLocaleString())}{' '}
                  <button
                    type="button"
                    className="underline decoration-dotted hover:text-foreground"
                    onClick={() => odhacz.mutate({ krok: 'MASZYNY_KOMPLET', wlaczone: false })}
                  >
                    {t('ss.cofnij')}
                  </button>
                </p>
              );
            })()}
          </div>
        </Krok>

        {/* ── 3 ───────────────────────────────────────────────────────────── */}
        <Krok numer={3} krok={krok('TRYB')} tytul={t('ss.k3.title')} poCo={t('ss.k3.poCo')}>
          {d.wyspa.tryb === 'SMART_SYNC' ? (
            <p className="text-[11px] text-muted-foreground">{t('ss.k3.juzWTrybie')}</p>
          ) : (
            <Button
              size="sm"
              disabled={!krok('TRYB').wolno || przelaczTryb.isPending}
              onClick={async () => {
                const tytul = t('ss.k3.przelacz');
                if (await zapytaj({ tytul, tresc: t('ss.k3.pytanie'), etykietaPotwierdz: tytul })) przelaczTryb.mutate();
              }}
            >
              {przelaczTryb.isPending ? <Loader2 className="size-4 animate-spin" /> : <Database className="size-4" />}
              {t('ss.k3.przelacz')}
            </Button>
          )}
        </Krok>

        {/* ── 4 ───────────────────────────────────────────────────────────── */}
        <Krok numer={4} krok={krok('KLUCZ')} tytul={t('ss.k4.title')} poCo={t('ss.k4.poCo')}>
          <div className="space-y-3">
            <TokenChmurowegoMastera />
            {/*
              ══════════════════════════════════════════════════════════════════════════════════
               POTWIERDZENIE SIDECARA TUTAJ, NIE W AGENTACH.

               ⚠️⚠️ ZGŁOSZONE 01.09.2026: „czy mamy na ekranie smart_sync potwierdzanie agenta
               chmurowego mssql, czy trzeba iść do zakładki agenci?". Trzeba było iść — a to jedyny
               krok tego kreatora, którego nie dało się wykonać na jego własnym ekranie. Do
               potwierdzenia portal przyjmuje od węzła WYŁĄCZNIE sygnał życia, więc bez tego
               kliknięcia kolejne kroki stoją i nic tego nie tłumaczy.

               ⚠️ POKAZUJEMY DOPIERO, GDY SIDECAR SIĘ ZGŁOSIŁ (`chmura`) I CZEKA: przycisk przy
               nieistniejącym węźle sugerowałby, że to my czegoś nie zrobiliśmy, a czeka się na
               BillVisa.
              ══════════════════════════════════════════════════════════════════════════════════
            */}
            {chmura !== undefined && chmura.acceptance !== 'POTWIERDZONY' && (
              <div className="flex flex-wrap items-center gap-2 rounded border border-[var(--warning)]/40 p-2">
                <span className="text-xs">
                  {t('ss.k4.czekaNaPotwierdzenie').replace('{wezel}', chmura.nodeId)}
                </span>
                <Button size="sm" disabled={potwierdz.isPending} onClick={() => potwierdz.mutate(chmura.id)}>
                  {potwierdz.isPending ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                  {t('ss.k4.potwierdz')}
                </Button>
              </div>
            )}
          </div>
        </Krok>

        {/* ── 5 ───────────────────────────────────────────────────────────── */}
        <Krok numer={5} krok={krok('BAZA_DO_CHMURY')} tytul={t('ss.k5.title')} poCo={t('ss.k5.poCo')}>
          <div className="space-y-3">
            {/*
              ⚠️⚠️ CAŁA ROBOTA NA TYM EKRANIE (zgłoszone 31.08.2026). Wgranie pliku, lista tego, co
              leży w magazynie, i wysłanie wybranej paczki na węzeł chmurowy — bez wychodzenia
              z kreatora. Kopiowanie z maszyny zostało z tego ekranu ZDJĘTE: to inna robota, robi
              się ją rzadko i ma własny ekran, do którego jest odnośnik pod spodem.

              ⚠️ UKRYTE POLE PLIKU, bo natywnego `<input type="file">` nie da się ostylować jak
              resztę przycisków — ta sama sztuczka co na ekranie Kopie.
            */}
            <input
              ref={plikRef}
              type="file"
              accept=".zip,.bak"
              className="hidden"
              onChange={(e) => {
                const plik = e.target.files?.[0];
                /** ⚠️ Czyścimy pole OD RAZU — inaczej wybranie tego samego pliku drugi raz nic nie zrobi. */
                e.target.value = '';
                if (plik !== undefined && wezelDlaPaczki !== null) {
                  wgraj.mutate({ agentId: wezelDlaPaczki.id, plik });
                }
              }}
            />
            <div className="space-y-1">
              {/*
                ⚠️ WGRANIE PLIKU NIE JEST BRAMKOWANE STANEM KROKU, w odróżnieniu od wysłania na
                węzeł. Do magazynu instancji wolno wrzucić paczkę zawsze: to nasz magazyn, operacja
                jest odwracalna i nie dotyka maszyny klienta. Bramka siedzi tam, gdzie zaczyna się
                skutek na cudzym dysku.
              */}
              <Button
                size="sm"
                disabled={wezelDlaPaczki === null || wgraj.isPending}
                onClick={() => plikRef.current?.click()}
              >
                {wgraj.isPending ? <Loader2 className="size-4 animate-spin" /> : <FileUp className="size-4" />}
                {t('ss.k5.zPliku')}
              </Button>
              {/*
                ⚠️ PROCENT Z REALNIE WYSŁANYCH BAJTÓW (`xhr.upload.onprogress`), nie z animacji —
                paczka bazy to setki megabajtów i „kręciołek" nie odróżnia wysyłki od zawieszenia.
              */}
              {wysylka !== null && (
                <p className="text-[11px] tabular-nums text-muted-foreground">
                  {t('ss.k5.wysylka').replace('{plik}', wysylka.nazwa).replace('{procent}', String(wysylka.procent))}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">{t('ss.k5.zPlikuHint')}</p>
              {wezelDlaPaczki === null && (
                /** ⚠️ Powód blokady wprost: bez potwierdzonego węzła paczki nie ma czemu przypisać. */
                <p className="text-[11px] text-[var(--warning)]">{t('ss.k5.brakWezla')}</p>
              )}
            </div>

            {/* ── GOTOWE PACZKI W MAGAZYNIE ──────────────────────────────────────── */}
            <div className="space-y-1 border-t border-border pt-2">
              <p className="text-[11px] font-medium text-muted-foreground">{t('ss.k5.wMagazynie')}</p>
              {gotowePaczki.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">{t('ss.k5.brakKopii')}</p>
              ) : (
                <ul className="divide-y divide-border/60 rounded border border-border">
                  {gotowePaczki.slice(0, PACZEK_NA_EKRANIE).map((z) => {
                    const out = z.output as { nazwa?: string; size?: number } | null;
                    return (
                      <li key={z.id} className="flex flex-wrap items-center justify-between gap-2 p-2 text-[11px]">
                        <span className="min-w-0">
                          <span className="block truncate font-mono">{out?.nazwa ?? z.id}</span>
                          <span className="text-muted-foreground">
                            {/*
                              ⚠️ SKĄD PACZKA, MÓWIMY WPROST. „Wgrana z komputera" i „kopia z maszyny X"
                              to dwie różne rzeczy, a przy zasianiu mastera to jedyna informacja,
                              po której technik pozna swój plik.
                            */}
                            {z.kind === 'KOPIA_WGRANA'
                              ? t('ss.k5.zrodloPlik')
                              : t('ss.k5.zrodloMaszyna').replace('{wezel}', z.agent.nodeId)}
                            {typeof out?.size === 'number' ? ` · ${Math.round(out.size / 1_048_576)} MB` : ''}
                            {` · ${new Date(z.createdAt).toLocaleString()}`}
                          </span>
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={chmura === undefined || naChmure.isPending || !krok('BAZA_DO_CHMURY').wolno}
                          onClick={() => naChmure.mutate({ chmuraId: chmura!.id, zadanieId: z.id })}
                        >
                          {naChmure.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <CloudUpload className="size-4" />
                          )}
                          {t('ss.k5.naChmure')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {gotowePaczki.length > PACZEK_NA_EKRANIE && (
                /** ⚠️ Mówimy, że lista jest UCIĘTA — reszta jest na ekranie Kopie, nie zniknęła. */
                <p className="text-[11px] text-muted-foreground">
                  {t('ss.k5.wiecejPaczek').replace('{n}', String(gotowePaczki.length - PACZEK_NA_EKRANIE))}
                </p>
              )}
              {chmura === undefined && (
                <p className="text-[11px] text-[var(--warning)]">{t('ss.k5.brakChmury')}</p>
              )}
            </div>

            {/*
              ⚠️ ODNOŚNIK ZOSTAJE JAKO DRUGA DROGA, nie jako główna: tam robi się kopię z maszyny,
              przerywa zawieszone zadania i widzi historię. Wprost ze zgłoszenia: „daj przycisk
              «lub Przejdź do kopii zapasowych» i tam sobie ktoś musi już ogarnąć i wrócić".
            */}
            <p className="flex items-center gap-1.5 border-t border-border pt-2 text-[11px] text-muted-foreground">
              {t('ss.k5.lub')}
              {/* ⚠️ `asChild`, żeby nie zagnieżdżać `<button>` w `<a>` — Slot oddaje style przyciskowi-linkowi. */}
              <Button size="sm" variant="outline" asChild>
                <Link to="/kopie">
                  <DatabaseBackup className="size-4" />
                  {t('ss.k5.doKopii')}
                </Link>
              </Button>
            </p>
          </div>
        </Krok>

        {/* ── 6 ───────────────────────────────────────────────────────────── */}
        <Krok numer={6} krok={krok('MASTER')} tytul={t('ss.k6.title')} poCo={t('ss.k6.poCo')}>
          {d.wyspa.masterNodeId !== null ? (
            <p className="text-[11px] text-muted-foreground">
              {t('ss.k6.stoi').replace('{wezel}', d.wyspa.masterNodeId)}
            </p>
          ) : (
            <div className="space-y-2">
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium">{t('ss.k6.nazwa')}</span>
                <span className="text-[11px] leading-snug text-muted-foreground">
                  {t('ss.k6.nazwaWhat').replace('{baza}', d.wyspa.masterDatabase ?? '—')}
                </span>
                <Input
                  className="h-8 max-w-xs"
                  value={bazaDocelowa}
                  onChange={(e) => setBazaDocelowa(e.target.value)}
                  placeholder={d.wyspa.masterDatabase ?? ''}
                />
              </label>
              {bazaDocelowa.trim() !== '' && !nazwaZgodna && (
                <p className="text-[11px] text-[var(--warning)]">{t('ss.k6.nazwaInna')}</p>
              )}
              <Button
                size="sm"
                disabled={
                  paczka === null || !nazwaBazyOk || !nazwaZgodna || odtworz.isPending || !krok('MASTER').wolno
                }
                onClick={async () => {
                  /** ⚠️ Groźne: drugiego zasiania pod tą nazwą nie będzie („BEZ ODWROTU" w treści). */
                  const tytul = t('ss.k6.odtworz');
                  const tresc = t('ss.k6.pytanie').replace('{baza}', bazaDocelowa.trim());
                  if (await zapytaj({ tytul, tresc, etykietaPotwierdz: tytul, ton: 'grozne' })) {
                    odtworz.mutate({ chmuraId: chmura!.id, nazwa: paczka!.nazwa });
                  }
                }}
              >
                {odtworz.isPending ? <Loader2 className="size-4 animate-spin" /> : <Database className="size-4" />}
                {t('ss.k6.odtworz')}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                {paczka === null ? t('ss.k6.brakPaczki') : t('ss.k6.paczka').replace('{nazwa}', paczka.nazwa)}
              </p>
            </div>
          )}
        </Krok>

        {/* ── 7 ───────────────────────────────────────────────────────────── */}
        <Krok
          numer={7}
          krok={krok('TEST_I_ROZSYLANIE')}
          tytul={t('ss.k7.title')}
          poCo={t('ss.k7.poCo')}
        >
          <div className="space-y-2">
            <ul className="space-y-1">
              {(krok('TEST_I_ROZSYLANIE').sprawdzenia ?? []).map((s) => (
                <li key={s.nazwa} className="flex items-start gap-2 text-xs">
                  {/*
                    ════════════════════════════════════════════════════
                     TRZY STANY, NIE DWA: zielone, żółte („świeci, ale nie blokuje") i czerwone.

                     ⚠️⚠️ DOŁOŻONE 09.09.2026. Do tej pory każde niespełnione sprawdzenie było
                     czerwone I trzymało zamek — także rozjazd w tabelach, których żaden przycisk
                     portalu nie rusza. Malowanie go dalej na czerwono przy czynnym przycisku
                     znaczyłoby ekran, który mówi „napraw to najpierw" i jednocześnie pozwala iść dalej.
                    ════════════════════════════════════════════════════
                  */}
                  <span
                    className={
                      s.ok
                        ? 'text-[var(--success)]'
                        : s.blokuje === false
                          ? 'text-[var(--warning)]'
                          : 'text-[var(--destructive)]'
                    }
                  >
                    {s.ok ? <Check className="mt-0.5 size-3.5" /> : <AlertTriangle className="mt-0.5 size-3.5" />}
                  </span>
                  <span className="min-w-0">
                    {s.nazwa}
                    <span className="text-muted-foreground"> — {s.szczegol}</span>
                    {!s.ok && s.blokuje === false && (
                      <span className="ml-1 rounded border border-border px-1 text-[10px] text-muted-foreground">
                        {t('ss.k7.nieBlokuje')}
                      </span>
                    )}
                    {/*
                      ⚠️⚠️ PRZYCISK SZCZEGÓŁÓW PRZY TYM JEDNYM SPRAWDZENIU — zgłoszone 01.09.2026:
                      „musimy w tym punkcie dokładniej pokazywać, co jest źle, bo to dość
                      enigmatyczne (…) możemy jedynie kliknąć synchronizację bębnów i wierzyć, że
                      się nie rozjebie". Portal miał te dane w tej samej odpowiedzi i ich nie rysował.

                      ⚠️ POKAZUJEMY GO TAKŻE, GDY SPRAWDZENIE JEST ZIELONE, ale rozjazd istnieje
                      (świeży, jeszcze „w drodze") — bo wtedy właśnie warto zobaczyć, czy to
                      naprawdę tylko propagacja.
                    */}
                    {(s.nazwa === NAZWA_SPRAWDZENIA_ROZJAZDU || s.nazwa === NAZWA_SPRAWDZENIA_POZA_WYROWNANIEM) &&
                      d.rozjazdy.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-1 h-5 px-1 text-[11px]"
                        onClick={() => setRozjazdOtwarty(true)}
                      >
                        <Search className="size-3" />
                        {t('rozj.pokaz')}
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {d.wyspa.wydaniaRodzenstwa ? (
              <div className="space-y-2">
                <p className="text-[11px] text-muted-foreground">{t('ss.k7.juzWlaczone')}</p>
                {/*
                  ══════════════════════════════════════════════════════════════════════════════
                   ZALEGŁE WYDANIA Z CZASU INSTALACJI — JEDEN PRZEBIEG, POTEM SAMA INFORMACJA.

                   ⚠️⚠️ ZAMÓWIONE 29.09.2026: „jak raz roześlemy tam, to opcja powinna po wdrożeniu
                   dawać już tylko info. Samą funkcjonalność możemy przenieść do drugiej zakładki".
                   Formularz stoi tu wyłącznie przed pierwszym przebiegiem; potem belka ze śladem
                   i odnośnik do „Synchronizacji", gdzie jest dosłanie, przerwanie i granica czasu.
                   Rozstrzyga `nadrobienie` z instancji — kreator nie liczy kliknięć.
                  ══════════════════════════════════════════════════════════════════════════════
                */}
                {d.wyspa.nadrobienie != null ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <PasekNadrobienia nadrobienie={d.wyspa.nadrobienie} />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1.5 text-[11px]"
                      onClick={() => ustawParametry({ zakladka: 'synchronizacje' }, { replace: true })}
                    >
                      <Wrench className="size-3" />
                      {t('ss.zakladka.synchronizacje')}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded border border-border p-2">
                    <FormularzNadrobienia groupId={groupId} />
                  </div>
                )}
              </div>
            ) : (
              <Button
                size="sm"
                disabled={!krok('TEST_I_ROZSYLANIE').wolno || wlaczRozsylanie.isPending}
                onClick={async () => {
                  const tytul = t('ss.k7.wlacz');
                  if (await zapytaj({ tytul, tresc: t('ss.k7.pytanie'), etykietaPotwierdz: tytul })) wlaczRozsylanie.mutate();
                }}
              >
                {wlaczRozsylanie.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                {t('ss.k7.wlacz')}
              </Button>
            )}
          </div>
        </Krok>
      </div>
      )}

      {zakladka === 'synchronizacje' && (
        <div className="space-y-3">
          {/*
            ══════════════════════════════════════════════════════════════════════════════════════
             KOLEJNOŚĆ PANELI — ułożona na nowo 23.09.2026: najpierw „co się dzieje", potem naprawa.

             ⚠️⚠️ ZGŁOSZONE WPROST: „aż boję się tu cokolwiek klikać przez samą ilość (i chyba
             redundancję funkcji)". Sprawdzanie i naprawa szły na przemian, a „Kadra na węzłach"
             dublowała stan tabel (wykrycie) i przebieg (naprawę). Kadrę usunięto — jej odczyt wiersz
             po wierszu stoi teraz przy wierszu w stanie tabel, i to dla każdej tabeli, nie tylko ludzi.

             ⚠️ POCZEKALNIA NA GÓRZE, bo odpowiada na pierwsze pytanie przy każdym zatorze: na kogo
             kolejka czeka. Przebieg OSTATNI — kolejkuje zapisy do baz klienta przez wiele godzin
             i nie może zapraszać do uruchamiania „na wszelki wypadek".
            ══════════════════════════════════════════════════════════════════════════════════════
          */}
          {/* ⚠️ Wstrzymane zdjęcia NAD wszystkim — czekają na decyzję; bez niej wyspa zostaje rozjechana. */}
          <WstrzymaneUsuniecia groupId={groupId} />

          <Poczekalnia groupId={groupId} />

          {/* ⚠️ Główny przegląd: od niego zaczyna się szukanie — i z jego wiersza rusza naprawa. */}
          <StanTabel
            groupId={groupId}
            rozjazdMiedzyWezlami={rozjazdMiedzyWezlami}
            onPokazRozjazd={() => setRozjazdOtwarty(true)}
            onUzgodnij={(sps) => {
              setDoUzgodnienia({ sps });
              przebiegRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />

          {/* ⚠️ Stan magazynowy — jedyny panel, który porównuje ILOŚCI, a nie liczby wierszy. */}
          <ZajetoscAutomatow groupId={groupId} />

          {/*
            ══════════════════════════════════════════════════════════════════════════════════════
             ODŚWIEŻENIE PORTALU Z BAZ — „przemiel to jeszcze raz" (do 23.09.2026 „Ponowny pełny odczyt").

             ⚠️⚠️ ZAMÓWIONE 18.09.2026 PO AWARII, W KTÓREJ PORTAL ODRZUCIŁ 288 WIERSZY STANU. Odrzucony
             wiersz nie wraca sam: portal odpowiada agentowi `2xx` także wtedy, gdy go odsieje, więc
             agent zapisuje u siebie „przyjęte" i ponowi go dopiero, gdy ktoś fizycznie ruszy towar
             w tej przegródce. Naprawa samej przyczyny zatrzymuje narastanie rozjazdu, ale go nie cofa.

             ⚠️⚠️ KIERUNEK W NAZWIE: bazy → portal. Nic nie zapisujemy na maszynach — przebieg niżej
             robi odwrotnie, i dwie naprawy o nazwach bez kierunku myliły się ze sobą.
            ══════════════════════════════════════════════════════════════════════════════════════
          */}
          <PelnyOdczytTabel groupId={groupId} />

          {/*
            ⚠️ ZALEGŁE WYDANIA — przeniesione z kreatora 29.09.2026 („samą funkcjonalność możemy
            przenieść do drugiej zakładki, jakby trzeba było faktycznie coś dosłać"). Przed przebiegiem,
            bo też dopisuje wiersze do baz klienta godzinami, ale tylko wydania — przebieg rusza całą resztę.
          */}
          <ZalegleWydania groupId={groupId} />

          <div ref={przebiegRef}>
            <PrzebiegUzgadniajacy groupId={groupId} wybor={doUzgodnienia} />
          </div>
        </div>
      )}

      {rozjazdOtwarty && <RozjazdSzczegoly groupId={groupId} d={d} onClose={() => setRozjazdOtwarty(false)} />}
    </div>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PONOWNY PEŁNY ODCZYT — wszystkich tabel albo wybranych, z całej wyspy albo z jednego węzła.
 *
 *  ⚠️⚠️ WYBÓR DOŁOŻONY 23.09.2026. Serwer od początku przyjmował listę tabel i jeden węzeł
 *  (`zlecenieOdczytu`), a ten panel zawsze wysyłał „wszystkie, cała wyspa". Na wyspie z tuzinem
 *  automatów to 26 tabel z każdej bazy — po to, żeby dociągnąć np. dwie tabele jednego automatu
 *  (kolumna, której portal nie zobaczył po zwężeniu, brakujący tryb w `PICAddressData`).
 *
 *  ⚠️ „NIC NIE WYBRANO" ZNACZY „WSZYSTKIE" — ta sama zasada co na serwerze, bo po ręcznej ingerencji
 *  nikt nie wie, która tabela ucierpiała. Zakres jest wypisany przed potwierdzeniem, żeby nikt nie
 *  poprosił o całość, myśląc, że prosi o jedną tabelę.
 *
 *  ⚠️ DWA KLIKNIĘCIA, bo to obciąża łącze automatu — a przycisk stoi na ekranie, po którym technik
 *  przewija w poszukiwaniu czego innego.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function PelnyOdczytTabel({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const [wToku, setWToku] = useState(false);
  const [pytanie, setPytanie] = useState(false);
  /** Osobne potwierdzenie dla przerwania — dwa różne pytania nie mogą dzielić jednego „tak". */
  const [pytanieOPrzerwanie, setPytanieOPrzerwanie] = useState(false);
  /** Wybrane tabele; pusto = wszystkie śledzone (patrz nagłówek). */
  const [wybrane, setWybrane] = useState<string[]>([]);
  /** `null` = cała wyspa. */
  const [wezel, setWezel] = useState<string | null>(null);

  /**
   * ⚠️ TE SAME KLUCZE ZAPYTAŃ CO RESZTA STRONY, więc panel nie kosztuje dodatkowych wywołań:
   * grupy tabel przychodzą z ekranu odstępów, węzły — z kreatora wdrożenia.
   */
  const tabele = useQuery({ queryKey: ['sync-tables', groupId], queryFn: () => fetchSyncTables(groupId) });
  const wdrozenie = useQuery({ queryKey: ['wdrozenie', groupId], queryFn: () => fetchWdrozenie(groupId) });
  const wezly = (wdrozenie.data?.wezly ?? []).filter((w) => w.potwierdzony && !w.odrzucony);

  const przelacz = (tabela: string): void =>
    setWybrane((w) => (w.includes(tabela) ? w.filter((x) => x !== tabela) : [...w, tabela]));

  const zakres = t('pelnyOdczyt.zakres')
    .replace('{tabele}', wybrane.length === 0 ? t('pelnyOdczyt.wszystkieTabele') : wybrane.join(', '))
    .replace('{wezel}', wezel ?? t('pelnyOdczyt.calaWyspa'));

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  PRZERWANIE TRWAJĄCEGO ODCZYTU — i to jest naprawa po awarii, którą wywołał przycisk obok
   *  (21.09.2026).
   *
   *  ⚠️⚠️ PROŚBA O PEŁNY ODCZYT JEST STANEM, NIE ZDARZENIEM: dopóki trwa, agent czyta CAŁE tabele
   *  w KAŻDYM cyklu. Na wyspie z 7 tys. pracowników zalało to kolejkę operacjami, a zatrzymać się
   *  dało wyłącznie zapytaniem do bazy instancji — czyli nie dało się nikomu, kto ma sam portal.
   *
   *  ⚠️ PRZYCISK, KTÓRY COŚ URUCHAMIA, MUSI MIEĆ ODPOWIEDNIK DO ZATRZYMANIA W TYM SAMYM MIEJSCU.
   *  Brak tego odpowiednika nie jest oszczędnością — jest zastawioną pułapką.
   *
   *  ⚠️⚠️ PRZERWANIE ZOSTAJE CAŁOŚCIOWE, choć proszenie ma już wybór. To jest zawór bezpieczeństwa:
   *  ma zatrzymać wszystko, co trwa, także odczyt, o który poproszono wcześniej innym wyborem albo
   *  przed przeładowaniem strony — a tego wyboru ekran już nie pamięta.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const przerwij = async (): Promise<void> => {
    setWToku(true);
    try {
      const w = await przerwijPelnyOdczyt(groupId, [], null);
      toast.success(
        t('pelnyOdczyt.przerwany')
          .replace('{tabel}', String(w.tabele.length))
          .replace('{wezlow}', String(w.wezly.length)),
      );
      setPytanieOPrzerwanie(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('pelnyOdczyt.nieUdaloSie'));
    } finally {
      setWToku(false);
    }
  };

  const zlec = async (): Promise<void> => {
    setWToku(true);
    try {
      const w = await poprosOPelnyOdczyt(groupId, wybrane, wezel);
      toast.success(
        t('pelnyOdczyt.zlecony')
          .replace('{tabel}', String(w.tabele.length))
          .replace('{wezlow}', String(w.wezly.length)),
      );
      setPytanie(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('pelnyOdczyt.nieUdaloSie'));
    } finally {
      setWToku(false);
    }
  };

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h2 className="text-sm font-medium">{t('pelnyOdczyt.tytul')}</h2>
      <p className="text-[11px] text-muted-foreground">{t('pelnyOdczyt.poCo')}</p>
      {/* ⚠️ Osobnym zdaniem, bo to jedyne pytanie, które ktoś zada przed kliknięciem. */}
      <p className="text-[11px] font-medium text-muted-foreground">{t('pelnyOdczyt.niePisze')}</p>

      {/* ⚠️ Wybór zamknięty na czas pytania — zakres w potwierdzeniu ma opisywać to, co pójdzie. */}
      {!pytanie && !pytanieOPrzerwanie && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              aria-pressed={wybrane.length === 0}
              onClick={() => setWybrane([])}
              className={cn(
                'rounded border px-1.5 py-0.5 text-[11px]',
                wybrane.length === 0 ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-accent',
              )}
            >
              {t('pelnyOdczyt.wszystkie')}
            </button>
          </div>
          {(tabele.data?.grupy ?? []).map((g) => (
            <div key={g.klucz} className="flex flex-wrap items-center gap-1">
              <span className="w-24 shrink-0 text-[11px] text-muted-foreground">{g.nazwa}</span>
              {g.tabele.map((x) => (
                <button
                  key={x.sps}
                  type="button"
                  aria-pressed={wybrane.includes(x.sps)}
                  onClick={() => przelacz(x.sps)}
                  className={cn(
                    'rounded border px-1.5 py-0.5 font-mono text-[11px]',
                    wybrane.includes(x.sps)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  {x.sps}
                </button>
              ))}
            </div>
          ))}
          <label className="flex items-center gap-2 text-[11px]">
            <span className="w-24 shrink-0 text-muted-foreground">{t('pelnyOdczyt.wezel')}</span>
            <select
              className="h-7 rounded border border-border bg-background px-1 text-[11px]"
              value={wezel ?? ''}
              onChange={(e) => setWezel(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">{t('pelnyOdczyt.calaWyspa')}</option>
              {wezly.map((w) => (
                <option key={w.nodeId} value={w.nodeId}>
                  {w.nodeId}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {pytanie ? (
        <div className="space-y-2">
          {/* ⚠️ Zakres PRZED kliknięciem — „wszystkie" i „jedna tabela" wyglądają tu tak samo, dopóki się ich nie nazwie. */}
          <p className="text-[11px] font-medium">{zakres}</p>
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={wToku} onClick={() => void zlec()}>
              {wToku ? <Loader2 className="size-4 animate-spin" /> : t('pelnyOdczyt.potwierdz')}
            </Button>
            <Button size="sm" variant="outline" disabled={wToku} onClick={() => setPytanie(false)}>
              {t('pelnyOdczyt.anuluj')}
            </Button>
          </div>
        </div>
      ) : pytanieOPrzerwanie ? (
        <div className="space-y-2">
          {/* ⚠️ Cena wypisana PRZED kliknięciem: portal zapisze „dostarczone" mimo niedokończonego odczytu. */}
          <p className="text-[11px] font-medium text-destructive">{t('pelnyOdczyt.przerwanieCena')}</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="destructive" disabled={wToku} onClick={() => void przerwij()}>
              {wToku ? <Loader2 className="size-4 animate-spin" /> : t('pelnyOdczyt.przerwijPotwierdz')}
            </Button>
            <Button size="sm" variant="outline" disabled={wToku} onClick={() => setPytanieOPrzerwanie(false)}>
              {t('pelnyOdczyt.anuluj')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPytanie(true)}>
            {t('pelnyOdczyt.przycisk')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPytanieOPrzerwanie(true)}>
            {t('pelnyOdczyt.przerwij')}
          </Button>
        </div>
      )}
    </section>
  );
}
